import {RXOTextField, Colors} from '../assets/styles'
import React, {useCallback, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {makeStyles} from '@mui/styles'
import SimpleSpinner from './SimpleSpinner'
import {InputAdornment} from '@mui/material'
import {spaceExtraSmall} from '../assets/styles/spacing'

const useStyles = makeStyles(theme => ({
  input: props => {
    const retVal = {
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '& input[type=text]': {},
    }

    if (props.small) {
      retVal.padding = spaceExtraSmall
    }

    return retVal
  },
}))

function CurrencyInput(props) {
  const classes = useStyles(props)
  const label = props.isRequired ? `${props.label} *` : props.label

  // Internal state to track cents (e.g., 150 = $1.50)
  const [centsValue, setCentsValue] = useState(0)

  // Initialize from props.value
  useEffect(() => {
    if (typeof props.value === 'number' && !isNaN(props.value)) {
      setCentsValue(Math.round(props.value * 100))
    } else {
      setCentsValue(0)
    }
  }, [props.value])

  // Format cents as dollar string (e.g., 150 -> "1.50")
  const formatCents = useCallback(cents => {
    if (typeof cents !== 'number' || isNaN(cents)) {
      cents = 0
    }
    const dollars = Math.floor(cents / 100)
    const remainingCents = cents % 100
    return `${dollars}.${remainingCents.toString().padStart(2, '0')}`
  }, [])

  const handleKeyPress = useCallback(
    event => {
      // Only allow digits
      if (!/[0-9]/.test(event.key)) {
        event.preventDefault()
        return
      }

      event.preventDefault()

      const digit = parseInt(event.key)
      const newCentsValue = centsValue * 10 + digit

      // Prevent values that would exceed reasonable limits (e.g., $999,999.99)
      if (newCentsValue > 99999999) {
        return
      }

      setCentsValue(newCentsValue)

      // Convert back to dollars and call onChange
      const dollarValue = newCentsValue / 100
      if (props.onChange) {
        props.onChange(dollarValue)
      }
    },
    [centsValue, props.onChange],
  )

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault()

        const newCentsValue = Math.floor(centsValue / 10)
        setCentsValue(newCentsValue)

        // Convert back to dollars and call onChange
        const dollarValue = newCentsValue / 100
        if (props.onChange) {
          props.onChange(dollarValue)
        }
      }
    },
    [centsValue, props.onChange],
  )

  const handleFocus = useCallback(event => {
    // Select all text on focus for better UX
    event.target.select()
  }, [])

  return (
    <RXOTextField.FormTextField
      label={label}
      placeholder={props.placeholder}
      variant="outlined"
      fullWidth
      InputLabelProps={{
        shrink: true,
      }}
      inputProps={{
        className: classes.input,
        min: props.min,
        max: props.max,
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <span style={{color: Colors.rxrPrimaryColor}}>$</span>
          </InputAdornment>
        ),
        endAdornment: props.isLoading ? (
          <InputAdornment position="end">
            <SimpleSpinner />
          </InputAdornment>
        ) : undefined,
      }}
      type="text"
      disabled={props.isDisabled}
      onKeyPress={handleKeyPress}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      value={formatCents(centsValue)}
      helperText={props.helperText}
      error={props.error}
      readOnly
    />
  )
}

CurrencyInput.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  isRequired: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  helperText: PropTypes.string,
  isLoading: PropTypes.bool,
  small: PropTypes.bool,
  error: PropTypes.bool,
}

export default CurrencyInput
